import { FC, useEffect, useRef } from 'react';

import styles from './Decorations.module.css';
import { animations, attachAnimations, interactiveStyle } from '../../utils/interactions';
import Icon from '../Icon/Icon';

interface IconProps {
  icon: string;
  width?: number;
  height?: number;
  color?: string;
  opacity?: number;
}

interface DecorationsProps {
  leftIcon?: IconProps | string;
  rightIcon?: IconProps | string;
  className?: string;
}

const defaultIconProps = {
  width: 48,
  height: 48,
  opacity: 0.1,
};

const Decorations: FC<DecorationsProps> = ({
  leftIcon = 'mdi:code-braces',
  rightIcon = 'mdi:palette-swatch',
  className = '',
}) => {
  const decorationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cleanup = attachAnimations([
      {
        element: decorationRef.current,
        animation: animations.rock(decorationRef.current!),
        triggers: ['hover', 'touch'],
      },
    ]);

    return cleanup;
  }, []);

  const getIconProps = (icon: IconProps | string) => {
    if (typeof icon === 'string') {
      return {
        icon,
        width: defaultIconProps.width,
        height: defaultIconProps.height,
        style: { opacity: defaultIconProps.opacity },
        'aria-hidden': true,
        'aria-label': 'Decoration Icon',
      };
    }
    return {
      icon: icon.icon,
      width: icon.width || defaultIconProps.width,
      height: icon.height || defaultIconProps.height,
      style: {
        opacity: icon.opacity || defaultIconProps.opacity,
        color: icon.color,
      },
      'aria-hidden': true,
      'aria-label': 'Decoration Icon',
    };
  };

  return (
    <div
      ref={decorationRef}
      className={`${styles.decoration} ${className}`}
      style={interactiveStyle}
      aria-hidden="true"
    >
      <Icon {...getIconProps(leftIcon)} className={styles.decorationIconLeft} />
      <Icon {...getIconProps(rightIcon)} className={styles.decorationIconRight} />
    </div>
  );
};

export default Decorations;
